import React, {useCallback, useEffect, useMemo, useState} from "react";
import {observer} from "mobx-react";
import rootStore from "../../../stores/rootStore.js";
import {Alert, Badge, Button, Card, Col, Container, InputGroup, Row} from "react-bootstrap";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {taxAuthApi} from "../../../api/taxAuth.js";
import {Formik} from "formik";
import Form from "react-bootstrap/Form";
import * as yup from "yup";

function TaxAuthDashboard() {

    const [rowData, setRowData] = useState([]);
    const [taxAuthInfo, setTaxAuthInfo] = useState(null);
    //const navigate = useNavigate();
    const [columnDefs, setColumnDefs] = useState([
        {field: 'customerFullName', headerName: 'Customer', filter: true},
        {field: 'customerEmail', headerName: 'Customer Email', filter: true},
        {field: 'orderDescription', headerName: 'Description', filter: true},
        {field: 'orderDate', headerName: 'Order Date', filter: true},
        {field: 'id', headerName: 'Actions', cellRenderer: 'actionRenderer'}
    ]);

    const onGridReady = useCallback((params) => {
        taxAuthApi.getRecentOrders()
            .then(orders =>{
                console.log("setting row data", orders);
                setRowData(orders);
            });
    }, []);
    const defaultColDef = useMemo(() => {
        return {
            flex: 1,
            minWidth: 100,
            resizable: true,
        };
    }, []);

    const certSearchSchema = yup.object().shape({
        propertyIdentifier: yup.string().required('Property Identifier is required'),
        searchType: yup.string().required('Search Type is required'),
    });
    const components = useMemo(() => ({
        'actionRenderer': ActionRenderer,
    }), []);

    const billingStore = rootStore.taxAuthBillingStore;
    const authStore = rootStore.authenticationStore;
    useEffect(() => {
        billingStore.checkPayoutsEnabled();
        let authentication = authStore.authentication;
        taxAuthApi.getTaxAuthInfo(authentication.orgId)
            .then(taxAuthInfo => {
                setTaxAuthInfo(taxAuthInfo);
            });
    }, []);
    const statusLabel = taxAuthInfo
        ? (taxAuthInfo.online ? "Online" : "Offline")
        : "Checking...";

    return (
        <Container className="dashboard-shell">
            <div className="dashboard-hero" data-accent="muni">
                <div>
                    <div className="dashboard-eyebrow">Municipal Dashboard</div>
                    <h1>Orders at a glance</h1>
                    <p>Track incoming certificate requests and keep your tax authority online.</p>
                </div>
                <div className="dashboard-hero-actions">
                    <Button variant="primary" href="/taxAuth/orders">View Orders</Button>
                    <Button variant="outline-secondary" href="/taxAuth/billing">Billing</Button>
                </div>
            </div>

            {!billingStore.payoutEnabled && (
                <Alert variant={"warning"} className="mt-4">
                    You haven&apos;t set up your billing yet. Please <a href="/taxAuth/billing">complete billing setup</a> to enable payouts.
                </Alert>
            )}

            <Row className="mt-4 g-3">
                <Col md={4}>
                    <Card className="stat-card">
                        <Card.Body>
                            <div className="stat-label">Recent Orders</div>
                            <div className="stat-value">{rowData.length}</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="stat-card">
                        <Card.Body>
                            <div className="stat-label">Status</div>
                            <div className="stat-value">{statusLabel}</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="stat-card">
                        <Card.Body>
                            <div className="stat-label">Payouts</div>
                            <div className="stat-value">{billingStore.payoutEnabled ? "Enabled" : "Disabled"}</div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mt-4 g-4">
                <Col lg={8}>
                    <Card className="dashboard-panel">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <strong>Recent Orders</strong>
                            <Badge bg="light" text="dark">Updated just now</Badge>
                        </Card.Header>
                        <Card.Body>
                            <div className="ag-theme-alpine dashboard-grid" style={{ height: "400px", width: "100%" }}>
                                <AgGridReact
                                    rowData={rowData}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    components={components}
                                    onGridReady={onGridReady}
                                    paginationAutoPageSize={true}
                                    pagination={true}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={4}>
                    <Card className="dashboard-panel mb-4">
                        <Card.Header className="bg-white">
                            <strong>Generate a Tax Certificate</strong>
                        </Card.Header>
                        <Card.Body>
                            <p className="text-muted">Generate a certificate from your tax system for internal use.</p>
                            <Formik
                                validationSchema={certSearchSchema}
                                onSubmit={async () => {}}
                                initialValues={{
                                    propertyIdentifier: "",
                                    searchType: "pid",
                                }}
                            >
                                {({ handleSubmit, handleChange, touched, values, errors }) => (
                                    <Form noValidate onSubmit={handleSubmit}>
                                        <Row className="mb-3">
                                            <Form.Group as={Col} md="12" controlId="propertyIdentifier">
                                                <Form.Label>Property Identifier</Form.Label>
                                                <InputGroup hasValidation>
                                                    <Form.Control
                                                        type="text"
                                                        name="propertyIdentifier"
                                                        value={values.propertyIdentifier}
                                                        onChange={handleChange}
                                                        isInvalid={touched.propertyIdentifier && !!errors.propertyIdentifier}
                                                    />
                                                    {touched.propertyIdentifier && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.propertyIdentifier}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </InputGroup>
                                            </Form.Group>
                                        </Row>
                                        <Row className="mb-3">
                                            <Form.Group as={Col} md="12" controlId="searchType">
                                                <Form.Label>Search Type</Form.Label>
                                                <InputGroup hasValidation>
                                                    <Form.Select
                                                        aria-label="searchType"
                                                        name={"searchType"}
                                                        value={values.searchType}
                                                        onChange={handleChange}
                                                        isInvalid={touched.searchType && !!errors.searchType}
                                                    >
                                                        <option key={"pid"} value={"pid"}>PID</option>
                                                        <option key={"roll"} value={"roll"}>Roll Number</option>
                                                        <option key={"mhr"} value={"mhr"}>MHR</option>
                                                    </Form.Select>
                                                    {touched.searchType && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.searchType}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </InputGroup>
                                            </Form.Group>
                                        </Row>
                                        <Button style={{width: "100%"}} type={"submit"}>Search</Button>
                                    </Form>
                                )}
                            </Formik>
                        </Card.Body>
                    </Card>
                    <Card className="dashboard-panel">
                        <Card.Body>
                            <h6 className="mb-2">Current Status</h6>
                            {taxAuthInfo && taxAuthInfo.online && (
                                <Badge bg="success">Online</Badge>
                            )}
                            {taxAuthInfo && !taxAuthInfo.online && (
                                <>
                                    <Badge bg="danger" className="mb-2">Offline</Badge>
                                    {taxAuthInfo.offlineReason != null ? (
                                        <p className="text-muted mb-0">Offline Reason: {taxAuthInfo.offlineReason}</p>
                                    ) : (
                                        <p className="text-muted mb-0">Offline Reason: No reason has been set.</p>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>


    )
}

function ActionRenderer(props){
    console.log("props:",props);
    return (
        <div style={{width:'100%', textAlign:'center'}}>
            <a href={`/taxAuthority/order/${props.data.id}`}><i style={{fontSize:18}} className="bi bi-file-pdf"></i></a>&nbsp;
            <a href={`/taxAuthority/order/${props.data.id}`}><i style={{fontSize:18}} className="bi bi-search"></i></a>
        </div>
    )
}

export default observer(TaxAuthDashboard);
